import { defaultLang, supportedLangs } from '@geartracker/types';

import { loadTranslations, resolveSupportedLang } from './load-translations';

import * as Sentry from '@sentry/react';
import type { BackendModule } from 'i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// ----------------------------------------------------------------------

/**
 * Loads language bundles on demand instead of baking all five into the entry
 * chunk. `read` runs at init for the detected language and again on every
 * `changeLanguage`, so callers keep using i18next normally.
 */
const lazyTranslationsBackend: BackendModule = {
  type: 'backend',
  init: () => {
    // No configuration: `loadTranslations` owns the chunk map.
  },
  read: (lng, _namespace, callback) => {
    // i18next may ask for a region-qualified tag (`nonExplicitSupportedLngs`
    // accepts `fr-FR` as `fr`), so resolve before hitting the chunk map.
    loadTranslations(resolveSupportedLang(lng))
      .then((bundle) => callback(null, bundle))
      .catch((error: unknown) => {
        Sentry.captureException(error, {
          tags: { feature: 'translations' },
          extra: { lng },
        });
        callback(
          error instanceof Error ? error : new Error(String(error)),
          false,
        );
      });
  },
};

void i18n
  .use(lazyTranslationsBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        caches: ['localStorage'],
        // Clamp to a language we ship a bundle for. Doing it here (rather than
        // leaning on `fallbackLng`) is what keeps a session to a single chunk:
        // i18next preloads its fallback language eagerly, so an unresolved tag
        // would cost a second bundle for every non-English user.
        convertDetectedLanguage: resolveSupportedLang,
      },
      // Detection always yields a supported language and `pnpm i18n:check`
      // enforces identical key sets across the five files, so no fallback
      // bundle is needed. A key that slips through still reaches Sentry via
      // `missingKeyHandler` below.
      fallbackLng: false,
      // Load the resolved language only — never the `fr` + `fr-FR` pair.
      load: 'currentOnly',
      supportedLngs: supportedLangs,
      nonExplicitSupportedLngs: true,
      debug: false,
      ns: ['translations'],
      defaultNS: 'translations',
      saveMissing: true,
      missingKeyHandler: (lngs, _ns, key) => {
        const message = `Missing translation: ${key}`;
        console.warn(message);
        Sentry.captureMessage('Missing translation', {
          extra: {
            key,
            lngs,
          },
          level: 'warning',
          tags: {
            feature: 'translations',
          },
        });
      },
      interpolation: {
        escapeValue: false,
        skipOnVariables: false,
      },
    },
    () => {
      document.documentElement.lang =
        i18n.resolvedLanguage || i18n.language || defaultLang;
    },
  );

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
